// @ts-nocheck
import { Form } from "@shared/antd/form";
import { Select } from "@shared/antd/select";
import React, { createElement, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { FormField, injectMainStore, MainStoreInjected, Msg } from "@cuba-platform/react";
import { FormComponentProps, FormItemProps } from "antd/lib/form";
import { GetFieldDecoratorOptions } from "antd/lib/form/Form";
import { MetaPropertyInfo } from "@cuba-platform/rest/dist-node/model";
import { DEFAULT_DATE_PATTERN } from "../../util/Date/Date";
import { MultiFileUpload } from "../MultiFileUpload";
import debounce from "lodash.debounce";
import { useDictionary } from "../../shared/hooks/use-dictionary-fetch";
import { ReadonlyFieldOptionalContainer } from "./type";
import { Condition, ConditionsGroup } from "@cuba-platform/rest/dist-node/filter";

type EntitySelectExtraProps = {
  asyncSearch?: boolean;
  entityContainer?: string;
  sortEnabled?: boolean;
  conditions?: (searchValue: string) => Array<Condition | ConditionsGroup>;
  customView?: string;
  onChangeSelect?: ({ value, options, selectedItem }: { value: string; options: any[]; selectedItem: any }) => any;
  isNeedSearch?: boolean;
} & Record<string, any>;

type ReadonlyFieldProps = MainStoreInjected &
  FormComponentProps & {
  entityName: string;
  propertyName: string;
  optionsContainer?: ReadonlyFieldOptionalContainer;
  formItemKey?: string;
  formItemOpts?: FormItemProps;
  fieldDecoratorId?: string;
  getFieldDecoratorOpts?: GetFieldDecoratorOptions;
  disabled?: boolean;
  extraProps?: EntitySelectExtraProps;
  sortOption?: "base" | "order";
  style?: React.CSSProperties;
  format?: string | string[];
  customLabel?: JSX.Element;
  isManyFiles?: boolean;
  component?: React.ComponentType<any>; // 👈 можно прокинуть кастомный компонент
};

export const ReadonlyField: React.FC<ReadonlyFieldProps> = injectMainStore(
  observer((props) => {
    const {
      form,
      entityName,
      propertyName,
      optionsContainer,
      fieldDecoratorId,
      getFieldDecoratorOpts,
      formItemKey,
      disabled,
      extraProps,
      style,
      isManyFiles,
      format,
      customLabel,
      component,
      mainStore,
      sortOption = "base",
    } = props;

    const { getFieldDecorator } = form;

    const formItemOpts = { ...props.formItemOpts };

    if (!formItemOpts.label) {
      formItemOpts.label =
        customLabel ||
        createElement(Msg, {
          entityName,
          propertyName,
        });
    }

    const propertyInfo = getPropertyInfo(entityName, propertyName, mainStore);
    const isDate =
      propertyInfo &&
      (propertyInfo.type === "date" || propertyInfo.type === "dateTime");
    const isFile =
      propertyInfo && propertyInfo.type === "sys$FileDescriptor";
    const isToManyRelation =
      (propertyInfo && isToManyRelationFn(propertyInfo.cardinality)) ||
      isManyFiles;
    const isAssociation =
      propertyInfo && propertyInfo.attributeType === "ASSOCIATION";
    const isSelectField = !isFile && isAssociation && optionsContainer;

    const resolvedFormat =
      format || (isDate ? DEFAULT_DATE_PATTERN : undefined);

    const commonProps = {
      entityName,
      propertyName,
      disabled,
      optionsContainer,
      style,
      format: resolvedFormat,
      sortOption: sortOption,
      form: this?.props?.form,
      ...extraProps,
    };

    const mode = propertyInfo && getSelectMode(propertyInfo.cardinality);

    // 👇 логика выбора компонента
    let fieldComponent: React.ReactNode;

    if (component) {
      // если прокинули кастомный компонент
      fieldComponent = createElement(component, commonProps);
    } else if (isSelectField) {
      fieldComponent = createElement(EntitySelectField, {
        mode,
        optionsContainer,
        allowClear: getAllowClear(propertyInfo),
        ...commonProps,
      });
    } else if (isFile && isToManyRelation) {
      fieldComponent = createElement(MultiFileUpload, commonProps);
    } else {
      fieldComponent = createElement(FormField, commonProps);
    }

    return createElement(
      Form.Item,
      { key: formItemKey || propertyName, ...formItemOpts },
      getFieldDecorator(
        fieldDecoratorId || propertyName,
        getFieldDecoratorOpts,
      )(fieldComponent),
    );
  }),
);

const getAllowClear = (propertyInfo: any) => {
  return !propertyInfo.mandatory;
};

const getSelectMode = (cardinality: string) => {
  if (cardinality === "ONE_TO_MANY" || cardinality === "MANY_TO_MANY") {
    return "multiple";
  }
  return "default";
};

const getPropertyInfo = (
  entityName: string,
  propertyName: string,
  mainStore: any,
): MetaPropertyInfo | null => {
  const metaClass = mainStore!.metadata!.find(
    (mci) => mci.entityName === entityName,
  );
  if (!metaClass) {
    return null;
  }
  return metaClass.properties.find((prop) => prop.name === propertyName) || null;
};

const isToManyRelationFn = (cardinality: string) => {
  return cardinality === "ONE_TO_MANY" || cardinality === "MANY_TO_MANY";
};

const EntitySelectField = observer((props: any) => {
  const {
    optionsContainer,
    entityContainer,
    sortOption,
    asyncSearch,
    fetcher,
    queryKey,
    form,
    propertyName,
    ...rest
  } = props;
  const [searchValue, setSearchValue] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
  const value = form?.getFieldValue(propertyName) || rest?.value;
  const sortEnabled = rest?.sortEnabled ?? true;
  const customConditions = rest?.conditions as Array<Condition | ConditionsGroup>;

  const conditions = useMemo(() => {
    if (initialLoaded && !touched && value) {
      return [
        { property: "id", value: String(value), operator: "=" } as any,
      ];
    }

    if (customConditions) {
      return customConditions(searchValue);
    }

    return [
      {
        group: "OR",
        conditions: [
          { property: "langValue1", value: searchValue, operator: "contains" },
          { property: "langValue2", value: searchValue, operator: "contains" },
          { property: "langValue3", value: searchValue, operator: "contains" },
        ],
      },
    ];
  }, [customConditions, initialLoaded, touched, value, searchValue]);

  const debouncedSearch = useMemo(() => {
    const d = debounce((v: string) => {
      setTouched(true);
      if (asyncSearch) setSearchValue(v);
    }, 600);
    return d;
  }, [asyncSearch]);

  const enabled = !!asyncSearch && !!entityContainer && (initialLoaded || touched);
  const viewSearch = rest?.customView || "_local";

  const { data: asyncItems, isFetching } = useDictionary(entityContainer, {
    conditions,
    enabled,
    loadOptions: {
      view: viewSearch,
      sort: sortEnabled ? "order" : undefined,
      limit: 100,
    },
  });

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (value && !initialLoaded) {
      setSearchValue(String(value));
      setInitialLoaded(true);
      setSearchValue("");
    }
  }, [value, initialLoaded]);

  const sortOrder = (a: any, b: any) => {
    const aHasOrder = typeof a.order === "number";
    const bHasOrder = typeof b.order === "number";
    if (aHasOrder && bHasOrder) return a.order - b.order;
    if (aHasOrder) return -1;
    if (bHasOrder) return 1;
    return a._instanceName.toLowerCase().localeCompare(b._instanceName.toLowerCase());
  };
  const baseOrder = (a: any, b: any) => a._instanceName.toLowerCase().localeCompare(b._instanceName.toLowerCase());
  const sortFn = sortOption === "order" ? sortOrder : baseOrder;

  const onDropdownVisibleChange = (open: boolean) => {
    if (open) setTouched(true);
  };

  const onSearch = (v: string) => {
    debouncedSearch(v);
    if (rest.onSearch) rest.onSearch(v);
  };
  const onChange = (value: any, option: any) => {
    setTouched(true);

    if (!value) {
      setSearchValue("");
    }

    const items = itemsToRender?.items || [];

    const selectedItem = items.find((item: any) => item.id === value);

    if (rest?.onChangeSelect) {
      rest.onChangeSelect({ value, items, selectedItem });
    }

    if (rest.onChange) {
      rest.onChange(value, option);
    }
  };

  const itemsToRender = asyncSearch ? asyncItems : optionsContainer || [];
  const isNeedSearch = rest?.isNeedSearch ?? true;

  useEffect(() => {
    if (asyncSearch && asyncItems && optionsContainer) {
      optionsContainer.items = asyncItems?.items || [];
    }
  }, [asyncItems, asyncSearch, optionsContainer]);

  return createElement(
    Select,
    {
      ...rest,
      loading: optionsContainer?.status === "LOADING" || isFetching,
      notFoundContent:
        optionsContainer?.status === "LOADING" || isFetching
          ? "Загрузка..."
          : null,
      optionFilterProp: "children",
      showSearch: isNeedSearch,
      onSearch,
      filterOption: !asyncSearch,
      onDropdownVisibleChange,
      onChange,
    },
    itemsToRender?.items
      ?.slice()
      .sort(sortFn)
      // eslint-disable-next-line react/display-name
      .map((entity: any) =>
        createElement(
          Select.Option,
          {
            value: entity.id,
            key: entity.id,
          },
          entity._instanceName,
        ),
      ),
  );
});
